/**
 * タイムスタンプパースユーティリティ
 *
 * サポートする形式:
 * - yyyyMMddHHmmssSSS（17桁: ミリ秒付き）
 * - yyyyMMddHHmmss（14桁）
 * - yyyy-MM-dd HH:mm:ss（標準形式）
 *
 * API のタイムスタンプは UTC で返されるため、Date.UTC() でパースする。
 */

/**
 * 各種タイムスタンプ文字列を Date (UTC) に変換する。
 * パースできない場合は null を返す。
 */
export const parseTimestamp = (value: string): Date | null => {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();

  // yyyyMMddHHmmssSSS (17桁)
  if (/^\d{17}$/.test(trimmed)) {
    const date = new Date(
      Date.UTC(
        Number(trimmed.slice(0, 4)),
        Number(trimmed.slice(4, 6)) - 1,
        Number(trimmed.slice(6, 8)),
        Number(trimmed.slice(8, 10)),
        Number(trimmed.slice(10, 12)),
        Number(trimmed.slice(12, 14)),
        Number(trimmed.slice(14, 17)),
      ),
    );
    return Number.isNaN(date.getTime()) ? null : date;
  }

  // yyyyMMddHHmmss (14桁)
  if (/^\d{14}$/.test(trimmed)) {
    const date = new Date(
      Date.UTC(
        Number(trimmed.slice(0, 4)),
        Number(trimmed.slice(4, 6)) - 1,
        Number(trimmed.slice(6, 8)),
        Number(trimmed.slice(8, 10)),
        Number(trimmed.slice(10, 12)),
        Number(trimmed.slice(12, 14)),
      ),
    );
    return Number.isNaN(date.getTime()) ? null : date;
  }

  // yyyy-MM-dd HH:mm:ss（標準形式）→ UTC として解釈
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(trimmed)) {
    const date = new Date(`${trimmed.replace(" ", "T")}Z`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  // ISO 8601（Z / オフセット付き）を許可
  if (
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/.test(
      trimmed,
    )
  ) {
    const date = new Date(trimmed);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
};

/**
 * タイムスタンプを "HH:mm" 形式の文字列に変換する。
 * パースできない場合は空文字列を返す。
 */
export const formatTime = (value: string): string => {
  const date = parseTimestamp(value);
  if (date === null) {
    return "";
  }
  const h = String(date.getHours()).padStart(2, "0");
  const m = String(date.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
};

/**
 * 2つの日付が異なる日かどうか判定する。
 * string を渡した場合は parseTimestamp でパースする。パースできない場合は false。
 */
export const isDifferentDay = (a: Date | string, b: Date | string): boolean => {
  const dateA = typeof a === "string" ? parseTimestamp(a) : a;
  const dateB = typeof b === "string" ? parseTimestamp(b) : b;
  if (dateA === null || dateB === null) {
    return false;
  }
  return (
    dateA.getFullYear() !== dateB.getFullYear() ||
    dateA.getMonth() !== dateB.getMonth() ||
    dateA.getDate() !== dateB.getDate()
  );
};

/**
 * メッセージ日付セパレーター用のラベルを「◯月◯日 (曜日)」形式で返す。
 * ロケールに追従する（ja: 「5月29日 (金)」 / en: 「May 29 (Fri)」）。
 */
export const formatDateFromDate = (date: Date, locale = "ja"): string => {
  const monthDay = new Intl.DateTimeFormat(locale, {
    month: "long",
    day: "numeric",
  }).format(date);
  const weekday = new Intl.DateTimeFormat(locale, { weekday: "short" }).format(
    date,
  );
  return `${monthDay} (${weekday})`;
};
